import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useIntl } from 'react-intl';
import { useToast } from '@repo/design-system-kit';
import { useSentry } from '@repo/shared-kit/sentry';
import { shouldSendToSentry } from '@repo/shared-kit/utils';
import { useCurrentDevice } from '@repo/shared-kit/hooks';
import { usersQueries } from '@repo/shared-kit/api/current-user';
import { useContractSignWebSocket } from '@repo/customer-registration-kit/contract-sign';
import { registerPartnersMutations } from '@repo/api-kit/queries/register-partners';
import { appJsURL } from '@repo/shared-kit/constants/hosts';
import { useNavigate } from '@repo/poly-hooks/routing';
import { fetchNavigationOrganizationsClient } from '../../utils/fetch-navigation-organizations';

export interface UseSubmitRegistrationOptions {
  registrationId: string;
  organizationId: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export interface UseSubmitRegistrationResult {
  submit: () => Promise<void>;
  submitAsync: () => Promise<void>;
  isSubmitting: boolean;
  isContractSignPending: boolean;
}

export function useSubmitRegistration({
  registrationId,
  organizationId,
  onError,
}: UseSubmitRegistrationOptions): UseSubmitRegistrationResult {
  const sentry = useSentry();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { showToast } = useToast();
  const { isMobile } = useCurrentDevice();
  const { data: currentUserData } = useQuery(usersQueries.getMe());
  const session = useEmberService('session');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractSignWebSocketEnabled, setContractSignWebSocketEnabled] = useState(false);

  const redirectAfterSubmit = useCallback(async () => {
    if (isMobile) {
      navigate(`/registrations/already-created/${organizationId}`);
      return;
    }

    const organizations = await fetchNavigationOrganizationsClient();
    const organization = organizations.find(org => org.id === organizationId);

    if (!organization) {
      throw new Error(`Organization with id ${organizationId} not found`);
    }

    window.location.replace(`${appJsURL}/organizations/${organization.slug}`);
  }, [isMobile, organizationId, navigate]);

  const { mutateAsync: submitRegistration } = useMutation(registerPartnersMutations.submit());
  const { mutateAsync: submitRegistrationAsync } = useMutation(
    registerPartnersMutations.submitAsync()
  );

  const handleContractSignSuccess = useCallback(() => {
    setContractSignWebSocketEnabled(false);
    void redirectAfterSubmit();
  }, [redirectAfterSubmit]);

  const handleContractSignValidationError = useCallback(() => {
    setContractSignWebSocketEnabled(false);
    setIsSubmitting(false);
    showToast({
      type: 'error',
      text: formatMessage({ id: 'errors.internal-server-error' }),
    });
    onError?.(new Error('Contract sign validation error'));
  }, [onError, showToast, formatMessage]);

  const handleContractSignTimeout = useCallback(() => {
    // Keep isSubmitting enabled during navigation
    // Note: useContractSignWebSocket clears the timeout internally, no need to disable it here
    navigate(`/registrations/${registrationId}/pending-contract`);
  }, [registrationId, navigate]);

  useContractSignWebSocket({
    token: session.data.authenticated.websocket_token ?? '',
    userId: currentUserData?.user?.id ?? '',
    onSuccess: handleContractSignSuccess,
    onValidationError: handleContractSignValidationError,
    onTimeout: handleContractSignTimeout,
    timeoutEnabled: contractSignWebSocketEnabled,
  });

  const submit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      await submitRegistration(registrationId);
      await redirectAfterSubmit();
    } catch (error) {
      showToast({
        type: 'error',
        text: formatMessage({ id: 'errors.internal-server-error' }),
      });
      if (shouldSendToSentry(error)) {
        sentry.captureException(error);
      }
      onError?.(error);
      setIsSubmitting(false);
    }
  }, [
    registrationId,
    submitRegistration,
    redirectAfterSubmit,
    onError,
    sentry,
    showToast,
    formatMessage,
  ]);

  const submitAsync = useCallback(async () => {
    setIsSubmitting(true);

    try {
      await submitRegistrationAsync(registrationId);
      setContractSignWebSocketEnabled(true);
    } catch (error) {
      showToast({
        type: 'error',
        text: formatMessage({ id: 'errors.internal-server-error' }),
      });
      if (shouldSendToSentry(error)) {
        sentry.captureException(error);
      }
      onError?.(error);
      setIsSubmitting(false);
    }
  }, [registrationId, submitRegistrationAsync, onError, sentry, showToast, formatMessage]);

  return {
    submit,
    submitAsync,
    isSubmitting,
    isContractSignPending: contractSignWebSocketEnabled,
  };
}
